import { useState, useEffect } from "react";
import { ExternalLink, Loader2, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import axios from "axios";
import API from "@/apiConfig";

const LOGO = "https://assets.wordaliveglobal.org/branding/wagc_logo_small.png";

export default function LinksPage() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/links`)
      .then((res) => setLinks(res.data.links || []))
      .catch(() => setError("Unable to load links. Please try again later."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      {/* Hero header */}
      <section className="bg-navy py-12 sm:py-16 text-center" data-testid="links-hero">
        <div className="max-w-lg mx-auto px-4">
          <img
            src={LOGO}
            alt="Word Alive Global Center"
            className="w-20 h-20 rounded-full mx-auto mb-4 border-2 border-gold/40 bg-white object-contain"
            data-testid="links-logo"
          />
          <h1
            className="font-heading text-3xl sm:text-4xl text-white mb-2"
            data-testid="links-heading"
          >
            Word Alive Global Center
          </h1>
          <p className="text-white/60 text-sm sm:text-base leading-relaxed">
            One Unified Body — Worship, Discipleship, Community
          </p>
        </div>
      </section>

      {/* Links list */}
      <section className="bg-bone min-h-[40vh] py-10 px-4" data-testid="links-section">
        <div className="max-w-lg mx-auto space-y-3">
          {loading && (
            <div className="flex justify-center py-16" data-testid="links-loading">
              <Loader2 className="w-6 h-6 text-navy/40 animate-spin" />
            </div>
          )}

          {error && (
            <div
              className="flex items-center gap-3 justify-center py-16 text-red-600 text-sm"
              data-testid="links-error"
            >
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && links.length === 0 && (
            <p className="text-center text-gray-400 py-16 text-sm" data-testid="links-empty">
              No links available right now.
            </p>
          )}

          {links.map((link) => (
            <LinkCard key={link.id} link={link} />
          ))}
        </div>
      </section>
    </Layout>
  );
}

function LinkCard({ link }) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-4 bg-white rounded-xl p-3 border border-gray-100
                 shadow-sm transition-all duration-200
                 hover:shadow-md hover:border-gold/30 hover:-translate-y-0.5
                 active:scale-[0.98]"
      data-testid={`link-card-${link.sort_order}`}
    >
      {link.thumbnail_url ? (
        <img
          src={link.thumbnail_url}
          alt={link.title}
          className="w-14 h-14 rounded-lg object-cover shrink-0 bg-bone"
          loading="lazy"
        />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-navy/5 flex items-center justify-center shrink-0">
          <ExternalLink className="w-5 h-5 text-navy/30" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <h2 className="font-semibold text-navy text-sm sm:text-base leading-tight truncate group-hover:text-gold transition-colors duration-200">
          {link.title}
        </h2>
        {link.description && (
          <p className="text-gray-500 text-xs sm:text-sm mt-0.5 line-clamp-2 leading-snug">
            {link.description}
          </p>
        )}
      </div>

      <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-gold shrink-0 transition-colors duration-200" />
    </a>
  );
}
